import { useCallback, useRef, useState } from "react";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import LocationOnRounded from "@mui/icons-material/LocationOnRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { facilityApi } from "@bw/shared/api";
import {
  geocodeAddress,
  useAddressAutocompleteController,
  type AddressResult,
  type AddressSuggestion,
} from "@bw/shared/components";
import type { FacilityPlaceSubmissionResponse } from "@bw/shared/types";

interface AddPlaceDialogProps {
  open: boolean;
  onClose: () => void;
  initialName?: string;
}

/** A place picked from Google Maps: its name and the resolved address. */
interface PickedPlace {
  name: string;
  address: AddressResult;
}

function messageFor(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "We couldn't submit this place. Please try again.";
}

/**
 * Add a missing place in three steps (approved flow): find it on Google Maps,
 * confirm the details, then thanks. Without Google Maps, or for a place Maps
 * doesn't know, the details step takes a typed address that is geocoded on
 * submit. The server reviews submissions and reuses duplicates.
 */
export function AddPlaceDialog({
  open,
  onClose,
  initialName = "",
}: AddPlaceDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState<"search" | "details">("search");
  const [picked, setPicked] = useState<PickedPlace | null>(null);
  // The chosen suggestion's name; a ref because Google resolves the address
  // in the same tick, before a state update would render.
  const pendingName = useRef(initialName);
  const [name, setName] = useState(initialName);
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<FacilityPlaceSubmissionResponse>();

  const onAddressSelect = useCallback(
    (resolved: AddressResult) => {
      setPicked({ name: pendingName.current, address: resolved });
      setName(pendingName.current);
      setStep("details");
    },
    [],
  );
  const search = useAddressAutocompleteController({
    onAddressSelect,
    defaultValue: initialName,
    label: "Search Google Maps",
  });
  const mapsAvailable = search.isAvailable;
  const currentStep = mapsAvailable ? step : "details";

  const close = () => {
    if (busy) return;
    onClose();
  };
  const lookupFailed = () =>
    setError("We couldn't load that place from Google Maps. Try again or enter it manually.");
  const choose = (suggestion: AddressSuggestion) => {
    pendingName.current = suggestion.main_text;
    setError(undefined);
    search.handleSuggestionSelect(suggestion).catch(lookupFailed);
  };
  // Enter selects through the controller; capture the highlighted (or first)
  // result's name first so the details step never keeps a stale name.
  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && search.suggestions.length) {
      pendingName.current =
        search.suggestions[search.activeIndex >= 0 ? search.activeIndex : 0].main_text;
      setError(undefined);
    }
    search.handleInputKeyDown(event).catch(lookupFailed);
  };
  const enterManually = () => {
    setPicked(null);
    setStep("details");
  };

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const resolved = picked?.address ?? (await geocodeAddress(address));
      const response = await facilityApi.submitPlace({
        name: name.trim(),
        address: {
          line1: resolved.address_line1,
          line2: null,
          city: resolved.city,
          state: resolved.state,
          postal_code: resolved.postal_code,
          country: resolved.country,
          latitude: resolved.lat,
          longitude: resolved.lng,
        },
        website_url: website.trim() || null,
        note: note.trim() || null,
      });
      setResult(response);
    } catch (submitError) {
      setError(messageFor(submitError));
    } finally {
      setBusy(false);
    }
  };

  const addressReady = picked ? true : address.trim().length >= 6;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      {result ? (
        <>
          <DialogTitle sx={{ pt: 5, textAlign: "center" }}>
            <CheckCircleRounded color="success" sx={{ fontSize: 52, display: "block", mx: "auto", mb: 1 }} />
            Thanks for adding this place
          </DialogTitle>
          <DialogContent sx={{ pb: 5 }}>
            <Stack spacing={2} sx={{ alignItems: "center", textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ maxWidth: 440 }}>
                {result.place.name} is {result.place.status === "pending"
                  ? "waiting for a quick review before it appears in search."
                  : "already in our place directory."}
              </Typography>
              {(result.possible_duplicates?.length ?? 0) > 0 && (
                <Alert severity="info" sx={{ width: "100%", textAlign: "left" }}>
                  We found an existing or nearby submission and reused it to avoid
                  creating a duplicate.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="contained" onClick={close} sx={{ borderRadius: 999 }}>Back to Play</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle component="div" sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {currentStep === "details" && mapsAvailable ? (
                <IconButton aria-label="Back to search" edge="start" onClick={() => setStep("search")} disabled={busy}>
                  <ArrowBackRounded />
                </IconButton>
              ) : null}
              <Box>
                <Typography component="h2" sx={{ fontSize: "1.0625rem", fontWeight: 700 }}>
                  Add a missing place
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mapsAvailable
                    ? currentStep === "search"
                      ? "Step 1 of 2 · find it on Google Maps"
                      : "Step 2 of 2 · confirm details"
                    : "Help players find courts and communities nearby."}
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {currentStep === "search" ? (
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <TextField
                  id={search.inputId}
                  inputRef={search.inputRef}
                  autoFocus
                  fullWidth
                  label="Search Google Maps"
                  value={search.value}
                  onChange={(event) => search.setValue(event.target.value)}
                  onFocus={search.handleInputFocus}
                  onKeyDown={onSearchKeyDown}
                  placeholder={search.isLoaded ? "Place name or address" : "Loading…"}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: search.suggestionsLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} aria-label="Searching places" />
                        </InputAdornment>
                      ) : undefined,
                    },
                  }}
                />
                {search.suggestions.length ? (
                  <List aria-label="Results from Google Maps" disablePadding>
                    {search.suggestions.map((suggestion) => (
                      <ListItemButton
                        key={suggestion.place_id}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => choose(suggestion)}
                        sx={{ gap: 1.5, borderRadius: 2, px: 1 }}
                      >
                        <LocationOnRounded color="action" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
                            {suggestion.main_text}
                          </Typography>
                          <Typography noWrap variant="body2" color="text.secondary">
                            {suggestion.secondary_text}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                          Select
                        </Typography>
                      </ListItemButton>
                    ))}
                  </List>
                ) : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Typography variant="body2" color="text.secondary">
                  Not on Google Maps?{" "}
                  <Link component="button" type="button" onClick={enterManually} sx={{ fontWeight: 600, verticalAlign: "baseline" }}>
                    Enter it manually
                  </Link>
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField required autoFocus label="Place name" value={name}
                  onChange={(event) => setName(event.target.value)} />
                {picked ? (
                  <Box sx={{ px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Full street address · from Google Maps</Typography>
                    <Typography>{picked.address.formatted_address}</Typography>
                  </Box>
                ) : (
                  <TextField required label="Full street address" value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    helperText="Used to place it on the map and check for duplicates." />
                )}
                <TextField label="Website (optional)" type="url" value={website}
                  onChange={(event) => setWebsite(event.target.value)} />
                <TextField label="What should players know? (optional)" multiline minRows={3}
                  value={note} onChange={(event) => setNote(event.target.value)} />
                <Alert severity="info" icon={<LocationOnRounded />}>
                  Submissions are reviewed before they appear. A business can claim and verify the place later.
                </Alert>
                {error && <Alert severity="error">{error}</Alert>}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={close} disabled={busy}>Cancel</Button>
            {currentStep === "details" ? (
              <Button variant="contained" onClick={() => void submit()} sx={{ borderRadius: 999 }}
                disabled={busy || name.trim().length < 2 || !addressReady}>
                {busy ? "Checking place…" : "Submit for review"}
              </Button>
            ) : null}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
